import { Search, Plus, PanelLeftClose } from 'lucide-react';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { useAppContext } from '@/contexts/AppContext.js';
import { Input } from '@/components/ui/input.js';
import { Button } from '@/components/ui/button.js';
import { PinnedSection } from './PinnedSection.js';
import { NotesSection } from './NotesSection.js';
import { BrowserSection } from './BrowserSection.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';

export function ExplorerSidebar() {
  const searchQuery = useArtifactPanelStore((state) => state.searchQuery);
  const setSearchQuery = useArtifactPanelStore((state) => state.setSearchQuery);
  const openBrowser = useArtifactPanelStore((state) => state.openBrowser);
  const registerArtifact = useArtifactPanelStore((state) => state.registerArtifact);
  const openTab = useArtifactPanelStore((state) => state.openTab);
  const toggleSidebar = useArtifactPanelStore((state) => state.toggleSidebar);

  const { createNewNote, currentNote } = useAppContext();

  const handleNewAction = (action: 'note' | 'browser') => {
    if (action === 'note') {
      // Create note in AppContext
      createNewNote();

      // Wait for state update and then open as artifact
      setTimeout(() => {
        if (currentNote) {
          const artifactId = `note-${currentNote.id}`;

          registerArtifact({
            id: artifactId,
            type: 'note',
            title: currentNote.title || 'Untitled Note',
            isPinned: false,
            lastOpened: Date.now(),
            createdAt: currentNote.createdAt,
            modifiedAt: currentNote.updatedAt,
            data: { noteId: currentNote.id },
          });

          openTab({
            id: artifactId,
            type: 'note',
            title: currentNote.title || 'Untitled Note',
            data: { noteId: currentNote.id },
          });
        }
      }, 0);
    } else if (action === 'browser') {
      openBrowser('', 'Browser');
    }
  };

  return (
    <div className="flex h-full flex-col border-r border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Artifacts</span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleSidebar}>
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Collapse sidebar</p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleNewAction('note')}>
                <span>New Note</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNewAction('browser')}>
                <span>New Browser Tab</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search artifacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          <PinnedSection />
          <NotesSection />
          <BrowserSection />

          {/* Coming Soon Sections */}
          <div className="pt-2">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Coming Soon
            </div>
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Files, Images, and Code viewers
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
